/**
 * Amplitude initialization functionality
 */ import * as amplitude from '@amplitude/analytics-browser';
import * as sessionReplay from '@amplitude/plugin-session-replay-browser';
import { getCvidCookie, getUserUuid, isWindowDefined } from '@chegg/utils';
import { capturePageAction, ActionSeverity } from '@chegg/observability';
import { setAmplitudeInitialized, getAmplitudeInitialized } from './amplitude-state';
import { getAmplitudeApiKey, APP_VERSION } from './utils';
import { setAmplitudeUserIdentity } from './identity';
import { createUnifiedAmplitudePlugin, resetPageViewEvent } from './plugin';
import { setupAmplitudeTokenListener } from './setup-token-listener';
import { exposeAmplitudeGlobally } from './amplitudeGlobal';
import { createPrivacyPlugin } from './privacy-plugin';
// Ensure LogLevel is available for the initialization
const { LogLevel } = amplitude.Types;
function completeAmplitudeInitialization() {
    try {
        const userId = getUserUuid() || '';
        const deviceId = getCvidCookie() || '';
        // Initialize Amplitude with the appropriate API key
        amplitude.init(getAmplitudeApiKey(), userId, {
            deviceId,
            appVersion: APP_VERSION,
            autocapture: {
                attribution: true,
                pageViews: true,
                sessions: true,
                elementInteractions: {
                    cssSelectorAllowlist: [
                        'a',
                        'button',
                        'input',
                        'select',
                        'textarea',
                        'label',
                        'video',
                        'audio',
                        '[contenteditable="true" i]',
                        '[data-amp-default-track]',
                        '.amp-default-track'
                    ],
                    dataAttributePrefix: 'data-test'
                },
                formInteractions: true,
                fileDownloads: true
            },
            logLevel: LogLevel.Warn
        });
        // Set user identity before initialization
        setAmplitudeUserIdentity();
        // Add the unified plugin that handles event enrichment
        amplitude.add(createUnifiedAmplitudePlugin());
        try {
            amplitude.add(sessionReplay.plugin({
                sampleRate: 1
            }));
        } catch (error) {
            capturePageAction({
                action: 'Error adding Session Replay plugin',
                severity: ActionSeverity.WARNING,
                location: 'amplitude-analytics'
            }, {
                error: String(error)
            });
        }
        exposeAmplitudeGlobally();
        // Set up automatic token listener for subscription status updates
        void setupAmplitudeTokenListener({
            enabled: true,
            onError: (error)=>{
                capturePageAction({
                    action: 'Token listener setup error',
                    severity: ActionSeverity.WARNING,
                    location: 'amplitude-analytics'
                }, {
                    error: error.message
                });
            }
        });
        setAmplitudeInitialized(true);
        console.debug('Amplitude initialized successfully', {
            userId,
            deviceId
        });
    } catch (error) {
        capturePageAction({
            action: 'Error during Amplitude initialization',
            severity: ActionSeverity.WARNING,
            location: 'amplitude-analytics'
        }, {
            error: String(error)
        });
    }
}
export function initializeAmplitude() {
    if (getAmplitudeInitialized()) {
        return;
    }
    if (!isWindowDefined) {
        return;
    }
    try {
        amplitude.add(createPrivacyPlugin(()=>{
            completeAmplitudeInitialization();
        }));
    } catch (error) {
        capturePageAction({
            action: 'Error setting up Privacy plugin',
            severity: ActionSeverity.WARNING,
            location: 'amplitude-analytics'
        }, {
            error: String(error)
        });
        completeAmplitudeInitialization();
    }
}
/**
 * Check if Amplitude is initialized
 */ export const isAmplitudeInitialized = ()=>getAmplitudeInitialized();
/**
 * Reset the initialization state (useful for testing)
 * @internal
 */ export function resetAmplitudeInitialization() {
    setAmplitudeInitialized(false);
    resetPageViewEvent();
}
